import * as React from 'react'

import { clsx } from 'clsx'

import {
  Card,
  ChannelIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  Flex,
  ExternalLinkIcon,
  PlayFilledIcon,
  Text,
  Tooltip,
  UnstyledButton,
  Link,
} from '@buffer-mono/popcorn'

import { PostMediaAsset_Asset } from '~publish/components/PostCard/PostMedia/PostMediaAsset'
import { readFragment } from '~publish/graphql'
import type { FragmentOf } from '~publish/graphql'
import { normalizeNetworkName } from '~publish/helpers/channels'
import { isOfType } from '~publish/helpers/typeGuards'
import { useIsVideoUrl } from '~publish/hooks/useIsVideoUrl'
import { getProxiedThumbnailUrl } from '~publish/legacy/utils/bufferImages'
import { isChannelIconService } from '~publish/pages/Community/util/isChannelIconService'
import type { CommentsPostCard_PostData } from '~publish/pages/Community/fragments/CommentsPostCard_Post'

import { useFormattedPostTime } from './useFormattedPostTime'
import { getPostDisplayText } from './utils'

import styles from './PostMiniatureReels.module.css'

import type { JSX } from 'react'

type ReelsHeaderProps = {
  showChannel: boolean
  channelService: CommentsPostCard_PostData['channelService']
  channelName?: string | null
  dueAt?: string | null
  formattedTime: string
}

function ReelsHeader({
  showChannel,
  channelService,
  channelName,
  dueAt,
  formattedTime,
}: ReelsHeaderProps): JSX.Element {
  return (
    <header className={styles.header}>
      <Flex align="center" gap="xs" className={styles.headerContent}>
        {showChannel && (
          <>
            <span
              className={styles.channelIcon}
              data-channel={channelService}
              aria-hidden
            >
              {isChannelIconService(channelService) && (
                <ChannelIcon type={channelService} size={10} />
              )}
            </span>
            <Text
              size="sm"
              weight="medium"
              className={styles.channelName}
              translate="no"
            >
              {channelName}
            </Text>
          </>
        )}
        {formattedTime && (
          <Text
            as="time"
            dateTime={dueAt ?? undefined}
            size="sm"
            weight="medium"
            className={styles.dueAt}
          >
            {formattedTime}
          </Text>
        )}
      </Flex>
    </header>
  )
}

type TypedAsset = FragmentOf<typeof PostMediaAsset_Asset>

/** URL for displaying the asset in an <img>: image source for ImageAsset, thumbnail only for VideoAsset (never video source). */
function getDisplayUrl(asset: TypedAsset | null | undefined): string | null {
  if (!asset) return null
  const data = readFragment(PostMediaAsset_Asset, asset)
  if (!data) return null
  if (isOfType(data, 'ImageAsset')) {
    const url = data.source ?? data.thumbnail ?? null
    if (!url) return null
    return data.image.isAnimated ? url : getProxiedThumbnailUrl(url)
  }
  if (isOfType(data, 'VideoAsset')) {
    return data.thumbnail ? getProxiedThumbnailUrl(data.thumbnail) : null
  }
  return data.thumbnail ?? data.source ?? null
}

function getVideoUrl(asset: TypedAsset | null | undefined): string | null {
  if (!asset) return null
  const data = readFragment(PostMediaAsset_Asset, asset)
  if (!data || !isOfType(data, 'VideoAsset')) return null
  return data.source ?? null
}

/* ---------------------------------------------------------------------------
 * Dot indicators for carousel navigation
 * ------------------------------------------------------------------------- */

function CarouselDots({
  total,
  currentIndex,
}: {
  total: number
  currentIndex: number
}): JSX.Element {
  const slidePositions = Array.from(
    { length: total },
    (_, slideIndex) => slideIndex + 1,
  )
  return (
    <div className={styles.dotsContainer} aria-hidden>
      {slidePositions.map((slidePosition) => (
        <span
          key={`reel-${slidePosition}-indicator`}
          className={styles.dot}
          data-active={slidePosition - 1 === currentIndex || undefined}
        />
      ))}
    </div>
  )
}

/* ---------------------------------------------------------------------------
 * useCarouselNavigation — carousel index state with keyboard support
 * ------------------------------------------------------------------------- */

function useCarouselNavigation(total: number): {
  currentIndex: number
  isFirst: boolean
  isLast: boolean
  goToPrevious: () => void
  goToNext: () => void
  handleKeyDown: (event: React.KeyboardEvent) => void
} {
  const [currentIndex, setCurrentIndex] = React.useState(0)

  const safeIndex = Math.min(currentIndex, Math.max(0, total - 1))

  React.useEffect(() => {
    if (currentIndex !== safeIndex) {
      setCurrentIndex(safeIndex)
    }
  }, [currentIndex, safeIndex])

  const isFirst = safeIndex === 0
  const isLast = safeIndex === total - 1

  const goToPrevious = React.useCallback(() => {
    if (!isFirst) setCurrentIndex((i) => i - 1)
  }, [isFirst])

  const goToNext = React.useCallback(() => {
    if (!isLast) setCurrentIndex((i) => i + 1)
  }, [isLast])

  const handleKeyDown = React.useCallback(
    (event: React.KeyboardEvent) => {
      if (event.key === 'ArrowLeft') {
        event.preventDefault()
        goToPrevious()
      } else if (event.key === 'ArrowRight') {
        event.preventDefault()
        goToNext()
      }
    },
    [goToPrevious, goToNext],
  )

  return {
    currentIndex: safeIndex,
    isFirst,
    isLast,
    goToPrevious,
    goToNext,
    handleKeyDown,
  }
}

/* ---------------------------------------------------------------------------
 * MediaContent — renders the current asset as video, image, or placeholder
 * ------------------------------------------------------------------------- */

function MediaContent({
  thumbnailUrl,
  videoUrl,
  isVideo,
  isPlaying,
  onEnded,
  altText,
}: {
  thumbnailUrl: string | null
  videoUrl: string | null
  isVideo: boolean
  isPlaying: boolean
  onEnded: () => void
  altText: string
}): JSX.Element {
  const [loaded, setLoaded] = React.useState(false)

  // Reset loaded state when the thumbnail URL changes (carousel navigation).
  const prevUrl = React.useRef(thumbnailUrl)
  if (prevUrl.current !== thumbnailUrl) {
    prevUrl.current = thumbnailUrl
    setLoaded(false)
  }

  if (isPlaying && isVideo && videoUrl) {
    return (
      <video
        src={videoUrl}
        poster={thumbnailUrl ?? undefined}
        className={styles.video}
        preload="metadata"
        controls
        // oxlint-disable-next-line @buffer/no-eager-media-download -- this element mounts only after the user presses play, so autoplay starts the video they asked for
        autoPlay
        muted
        playsInline
        aria-label={altText || 'Video'}
        onEnded={onEnded}
        crossOrigin="anonymous"
      />
    )
  }

  if (thumbnailUrl) {
    return (
      <img
        src={thumbnailUrl}
        alt={altText}
        className={clsx(
          styles.thumbnailImage,
          loaded && styles.thumbnailLoaded,
        )}
        loading="lazy"
        onLoad={() => setLoaded(true)}
      />
    )
  }

  return (
    <div className={styles.thumbnailPlaceholder}>
      <Text size="sm" color="subtle">
        No thumbnail
      </Text>
    </div>
  )
}

/* ---------------------------------------------------------------------------
 * PlayButton — triggers inline video playback
 * ------------------------------------------------------------------------- */

function PlayButton({ onPlay }: { onPlay: () => void }): JSX.Element {
  return (
    <button
      type="button"
      className={styles.reelsButton}
      aria-label="Play video"
      onClick={onPlay}
    >
      <PlayFilledIcon />
    </button>
  )
}

/* ---------------------------------------------------------------------------
 * ExternalLinkButton — links to the original post on the network when the
 * video cannot be played inline
 * ------------------------------------------------------------------------- */

type ExternalLinkButtonProps = {
  sourceUrl: string
  channelService: string
}

function ExternalLinkButton({
  sourceUrl,
  channelService,
}: ExternalLinkButtonProps): JSX.Element {
  const networkName = normalizeNetworkName(channelService)
  return (
    <Tooltip
      content={
        <Text as="span" size="sm" className={styles.tooltipContent}>
          This video was uploaded natively on {networkName}. Watch it there.
        </Text>
      }
      delay={1000}
    >
      <Link
        href={sourceUrl}
        target="_blank"
        rel="noopener noreferrer"
        className={styles.reelsButton}
        aria-label={`Watch on ${networkName}`}
      >
        <PlayFilledIcon className={styles.nativeVideoDefaultIcon} />
        <ExternalLinkIcon className={styles.nativeVideoHoverIcon} />
      </Link>
    </Tooltip>
  )
}

/* ---------------------------------------------------------------------------
 * ReelButton — decides whether to show a play button or an external link
 * ------------------------------------------------------------------------- */

type ReelButtonProps = {
  canPlay: boolean
  videoUrl: string | null
  channelService: string
  onPlay: () => void
}

function ReelButton({
  canPlay,
  videoUrl,
  channelService,
  onPlay,
}: ReelButtonProps): JSX.Element | null {
  if (canPlay) {
    return <PlayButton onPlay={onPlay} />
  }
  if (videoUrl) {
    return (
      <ExternalLinkButton
        sourceUrl={videoUrl}
        channelService={channelService}
      />
    )
  }
  return null
}

/* ---------------------------------------------------------------------------
 * ReelsMedia — composes video playback, carousel, and static thumbnail modes
 * ------------------------------------------------------------------------- */

type ReelsMediaProps = {
  assets: TypedAsset[]
  caption: string
  channelService: string
}

function ReelsMedia({
  assets,
  caption,
  channelService,
}: ReelsMediaProps): JSX.Element {
  const [isPlaying, setIsPlaying] = React.useState(false)

  const isCarousel = assets.length > 1
  const {
    currentIndex,
    isFirst,
    isLast,
    goToPrevious,
    goToNext,
    handleKeyDown,
  } = useCarouselNavigation(assets.length)

  const currentAsset = assets[currentIndex]
  const displayUrl = getDisplayUrl(currentAsset)
  const videoUrl = getVideoUrl(currentAsset)
  const { isVideo } = useIsVideoUrl({ url: videoUrl ?? '' })
  const canPlay = isVideo

  return (
    <figure className={styles.thumbnailWrapper}>
      {/* oxlint-disable-next-line jsx-a11y/no-static-element-interactions -- The carousel is a labelled composite widget with a tab stop and Arrow key navigation. */}
      <div
        className={styles.thumbnailInner}
        role={isCarousel ? 'region' : undefined}
        aria-roledescription={isCarousel ? 'carousel' : undefined}
        aria-label={
          isCarousel
            ? `Image carousel, ${currentIndex + 1} of ${assets.length}`
            : undefined
        }
        tabIndex={isCarousel ? 0 : undefined}
        onKeyDown={isCarousel ? handleKeyDown : undefined}
      >
        <MediaContent
          thumbnailUrl={displayUrl}
          videoUrl={videoUrl}
          isVideo={isVideo}
          isPlaying={isPlaying}
          onEnded={() => setIsPlaying(false)}
          altText={
            isCarousel ? `Image ${currentIndex + 1} of ${assets.length}` : ''
          }
        />

        {!isPlaying && (
          <ReelButton
            canPlay={canPlay}
            videoUrl={videoUrl}
            channelService={channelService}
            onPlay={() => setIsPlaying(true)}
          />
        )}

        {isCarousel && !isFirst && (
          <UnstyledButton
            className={clsx(styles.navButton, styles.navButtonLeft)}
            onClick={goToPrevious}
            aria-label="Previous image"
          >
            <ChevronLeftIcon size={16} />
          </UnstyledButton>
        )}
        {isCarousel && !isLast && (
          <UnstyledButton
            className={clsx(styles.navButton, styles.navButtonRight)}
            onClick={goToNext}
            aria-label="Next image"
          >
            <ChevronRightIcon size={16} />
          </UnstyledButton>
        )}

        {isCarousel && (
          <CarouselDots total={assets.length} currentIndex={currentIndex} />
        )}

        {!isPlaying && caption && (
          <figcaption
            className={clsx(
              styles.captionOverlay,
              isCarousel && styles.captionOverlayCarousel,
            )}
          >
            <Text
              as="p"
              size="sm"
              className={styles.captionText}
              translate="no"
            >
              {caption}
            </Text>
          </figcaption>
        )}
      </div>
    </figure>
  )
}

type PostMiniatureReelsProps = {
  post: CommentsPostCard_PostData
  showChannel?: boolean
  className?: string
}

export const PostMiniatureReels: React.FC<PostMiniatureReelsProps> = ({
  post,
  showChannel = true,
  className,
}) => {
  const { assets, channelService, channel, dueAt } = post
  const formattedTime = useFormattedPostTime(dueAt)

  const typedAssets: TypedAsset[] = assets ?? []
  const caption = getPostDisplayText(post)?.trim() ?? ''

  return (
    <Card asChild padding="none">
      <article
        className={clsx(styles.card, className)}
        aria-label={`Reel for ${
          channel?.displayName ?? channel?.name ?? 'unknown channel'
        }`}
        data-status={post.status}
      >
        <ReelsHeader
          showChannel={showChannel}
          channelService={channelService}
          channelName={channel?.displayName ?? channel?.name}
          dueAt={dueAt}
          formattedTime={formattedTime}
        />
        <ReelsMedia
          assets={typedAssets}
          caption={caption}
          channelService={channelService}
        />
      </article>
    </Card>
  )
}
